import { ROOT_URL, GRANT_TYPE } from '@/services/ApiService.const'

import {
  PERSON_CONTROL_ATTRIBUTES,
  PERSON_ATTRIBUTES,
  GENERIC_MODEL,
  CONFIG_MODEL,
  DOCUMENT_MODEL,
  CUSTOMER_FILE,
  TIMELINE_MODEL,
  DOCUMENT_PAGE_MODEL,
} from '@/services/models.js'

import JsonApi from 'devour-client'
import store from '@/store/index.js'
const jsonApi = new JsonApi({
  apiUrl: ROOT_URL,
  pluralize: false,
})

const context = {
  modalOpened: false,
  _onErrorSSO: null,
  _onErrorCredentials: null,
}

jsonApi.define('person', PERSON_ATTRIBUTES)
jsonApi.define('people', PERSON_ATTRIBUTES)
jsonApi.define('person_control', PERSON_CONTROL_ATTRIBUTES)
jsonApi.define('person_controls', PERSON_CONTROL_ATTRIBUTES)
jsonApi.define('kyc_general_configuration', CONFIG_MODEL)
jsonApi.define('kyc_general_configurations', CONFIG_MODEL)
jsonApi.define('person_kind_configurations', GENERIC_MODEL)
jsonApi.define('enums', GENERIC_MODEL)
jsonApi.define('genericModel', GENERIC_MODEL)
jsonApi.define('relation', GENERIC_MODEL)
jsonApi.define('supporting_document', DOCUMENT_MODEL)
jsonApi.define('supporting_documents', DOCUMENT_MODEL)
jsonApi.define('supporting_document_pages', DOCUMENT_PAGE_MODEL)
jsonApi.define('customer_file', CUSTOMER_FILE)
jsonApi.define('customer_files', CUSTOMER_FILE)
jsonApi.define('person_timeline', TIMELINE_MODEL)
jsonApi.define('timelines', TIMELINE_MODEL)

/**
  @todo change thoses ```.catch((error) => Promise.resolve(error))```
  it makes silent errors
 **/

const requestMiddleware = {
  name: 'add-bearer-token-if',
  req: async (payload) => {
    const token = localStorage.getItem('access_token')

    if (token) {
      payload.req.headers.Authorization = `Bearer ${token}`
    }

    return payload
  },
}

jsonApi.axios.interceptors.response.use(
  function(response) {
    return response
  },
  async function(error) {
    if (error.response.status === 401) {
      // inspiré par https://medium.com/swlh/handling-access-and-refresh-tokens-using-axios-interceptors-3970b601a5da
      const originalRequest = error.config
      if (context.modalOpened || originalRequest._isRetrying) {
        return
      }
      context.modalOpened = true
      if (!context._onErrorSSO && !context._onErrorCredentials) {
        throw Error('context._onError is not defined')
      }
      let result
      if (context._onErrorSSO) {
        const response = await context._onErrorSSO()
        store.commit('app/toggleAppLoader', false)
        context.modalOpened = false

        if (!response) {
          localStorage.clear()
          window.location.reload()

          return Promise.reject(error)
        }
        const tokenResponse = await requestToken({
          grant_type: GRANT_TYPE.refresh_token,
          refresh_token: localStorage.getItem('refresh_token'),
        })
        originalRequest._isRetrying = true
        originalRequest.headers.Authorization = `Bearer ${tokenResponse.access_token}`

        const originalResult = await jsonApi.axios(originalRequest)

        return Promise.resolve(originalResult)
      } else if (context._onErrorCredentials) {
        const response = await context._onErrorCredentials()
        store.commit('app/toggleAppLoader', false)
        context.modalOpened = false

        if (!response) {
          localStorage.clear()
          window.location.reload()

          return Promise.reject(error)
        }
        const tokenResponse = await requestToken({
          grant_type: GRANT_TYPE.refresh_token,
          refresh_token: localStorage.getItem('refresh_token'),
        })
        originalRequest._isRetrying = true
        originalRequest.headers.Authorization = `Bearer ${tokenResponse.access_token}`

        const originalResult = await jsonApi.axios(originalRequest)

        return Promise.resolve(originalResult)
      }
      context.modalOpened = false
      if (result.access_token) {
        store.commit('snackbar/addMessage', {
          type: 'success',
          text: 'auth_success',
        })
      } else {
        store.commit('snackbar/addMessage', {
          type: 'error',
          text: 'auth_error',
        })
        localStorage.clear()
        window.location.reload()
      }

      return Promise.reject(error)
    } else {
      const result = {}
      if (error.response.data.errors) {
        error.response.data.errors.forEach((item) => {
          if (!item || !item.meta || !item.meta.attribute) {
            return
          }
          result[item.meta.attribute] = [item.meta.error,]
        })
        const errors = { data: { errors: result, }, }

        return Promise.resolve(errors)
      } else {
        return Promise.reject(error.response.data)
      }
    }
  }
)

const setCallbackSSO = (onError) => {
  context._onErrorSSO = onError
}

const setCallbackCredentials = (onError) => {
  context._onErrorCredentials = onError
}

jsonApi.insertMiddlewareBefore('axios-request', requestMiddleware)

const baseUrl = ROOT_URL

const urls = {
  requestToken: 'api/oauth/token',
  logout: 'v1/oauth/revoke',
  connectToken: 'amundi_immo/sso/token',
  getStudies: '/amundi_immo/people/PERSON_ID/studies/',
  validatePersonFolder: '/amundi_immo/people/PERSON_ID/user_final_validation',
  getPerson:
    'amundi_immo/people/PERSON_ID?include=supporting_documents,supporting_documents.pages,person_controls',
  getCustomerFile: 'amundi_immo/customer_file/',
  getPersonControls: 'amundi_immo/person_controls/PERSON_ID',
  getConfiguration: 'amundi_immo/people/configurations',
  getPeopleList: 'amundi_immo/people?include=customer_files,person_controls',
}

const getEntryPointUrl = (type, id = null) => {
  if (id !== null) {
    return `${type}/${id}`
  }

  return type
}

const getUrl = (type, id = null, query = null) => {
  const entryPointUrl = getEntryPointUrl(type, id)
  const urlArray = [baseUrl, '/amundi_immo/', entryPointUrl,]
  if (query) {
    urlArray.push(`?${query}`)
  }

  return urlArray.join('')
}

const logout = async (params) => {
  let response = {}
  try {
    response = await jsonApi.axios.post(baseUrl + urls.logout, params)
  } catch (error) {
    return error
  }

  return response.data ? response.data : response
}

const requestToken = async (params) => {
  let response = {}
  try {
    response = await jsonApi.axios.post(baseUrl + urls.requestToken, params)
  } catch (error) {
    return error
  }

  if (response.data) {
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
  }

  return response.data ? response.data : response
}

const connectToken = async (params) => {
  let response = {}
  try {
    response = await jsonApi.axios.post(baseUrl + urls.connectToken, params)
  } catch (error) {
    return error
  }

  if (response.data) {
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
  }

  return response.data ? response.data : response
}

const validatePersonFolder = async (id) => {
  let response = {}
  try {
    response = await jsonApi.axios.post(
      baseUrl + urls.validatePersonFolder.replace('PERSON_ID', id),
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      }
    )
  } catch (error) {
    return error
  }

  return response.data ? response.data : response
}

const getPerson = async (params) => {
  const response = await jsonApi.findAll(urls.getPerson.replace('PERSON_ID', params.id))

  return response
}

const getPersonControls = async (params) => {
  const response = await jsonApi.findAll(urls.getPersonControls.replace('PERSON_ID', params.id))

  return response
}

const getPeopleList = async (params) => {
  let url = `${urls.getPeopleList}&page=${params.page}&per_page=${params.perPage}`
  if (params.search.length) {
    url += `&search=${params.search}`
  }
  const response = await jsonApi.findAll(url)

  return response
}

const downloadPage = async (idPage) => {
  const url = getUrl('supporting_document_pages', idPage)

  const response = await jsonApi.request(url, 'GET', null, {}).catch((error) => Promise.resolve(error))

  return response
}

const getCustomerFile = async (params) => {
  const url = getUrl('customer_files', params.id)
  const response = await jsonApi.request(url, 'GET', null, {}).catch((error) => Promise.resolve(error))

  return response
}

const postAdvisorFinalValidation = async (customerFileId) => {
  const url = [getUrl('customer_files', customerFileId), '/advisor_final_validation',].join('')

  const response = await jsonApi.request(url, 'POST', null, {}).catch((error) => Promise.resolve(error))

  return response
}

const getTimeline = async (params) => {
  let url = getUrl('people', params.id)
  url += '/timeline'
  const response = await jsonApi.request(url, 'GET', null, {}).catch((error) => Promise.resolve(error))

  return response
}

const getConfiguration = async () => {
  return await jsonApi.findAll(urls.getConfiguration)
}

const _patchItem = async (type, id, payload) => {
  const url = getUrl(type, id)

  const response = await jsonApi.request(url, 'PATCH', null, payload).catch((error) => Promise.resolve(error))

  return response.data ? response.data : response
}

const _postItem = async (type, id, payload) => {
  const url = getUrl(type, id)

  const response = await jsonApi.request(url, 'POST', null, payload).catch((error) => Promise.resolve(error))

  return response.data ? response.data : response
}

const createDocument = async (payload) => {
  const url = getUrl('supporting_documents')

  const response = await jsonApi.axios.post(url, payload, {
    headers: {
      Accept: 'application/vnd.api+json',
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data ? response.data : response
}

const deleteDocument = async (id) => {
  const url = getUrl('supporting_documents', id)

  const response = await jsonApi.request(url, 'DELETE', null, {}).catch((error) => Promise.resolve(error))

  return response.data ? response.data : response
}

const addPages = async (payload) => {
  const id = payload.id || payload.get('id')
  let url = getUrl('supporting_documents', id)
  url += '/relationships/pages'
  let response = {}

  try {
    response = await jsonApi.axios.put(url, payload.formData || payload, {
      headers: {
        Accept: 'application/vnd.api+json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'multipart/form-data',
      },
    })
  } catch (error) {
    return error
  }

  return response.data ? response.data : response
}

const getDeletePagePayload = (idPages) => {
  if (!Array.isArray(idPages)) {
    return getDeletePagePayload[idPages]
  }

  return idPages.map((id) => ({
    type: 'supporting_document_pages',
    id,
  }))
}

const deletePage = async (idDoc, idPages) => {
  let url = getUrl('supporting_documents', idDoc)
  url += '/relationships/pages'

  const payload = getDeletePagePayload(idPages)

  const response = await jsonApi
    .request(url, 'DELETE', null, payload)
    .catch((error) => Promise.resolve(error))

  return response.data ? response.data : response
}

const generateReport = async (email) => {
  const url = getUrl('customer_files', 'export', `mail_recipients=${email}`)
  const response = await jsonApi.request(url, 'GET', null, {}).catch((error) => Promise.resolve(error))

  return response
}

export default {
  getPerson,
  getPersonControls,
  getCustomerFile,
  postAdvisorFinalValidation,
  requestToken,
  connectToken,
  getConfiguration,
  _patchItem,
  createDocument,
  deleteDocument,
  addPages,
  deletePage,
  validatePersonFolder,
  downloadPage,
  _postItem,
  getTimeline,
  getPeopleList,
  setCallbackSSO,
  setCallbackCredentials,
  logout,
  generateReport,
}
